import { ReactNode, useCallback, useMemo } from 'react'

import { MessageDescriptor } from '@lingui/core'

import { LpToken } from '@cowprotocol/common-const'
import { getCurrencyAddress } from '@cowprotocol/common-utils'
import { getAddressKey } from '@cowprotocol/cow-sdk'
import { LpTokenProvider } from '@cowprotocol/types'
import { useWalletInfo } from '@cowprotocol/wallet'

import { msg, t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useInjectedWidgetParams } from 'entities/injectedWidget'

import { Field } from 'legacy/state/types'

import { SelectTokenWidget } from 'modules/tokensList'
import { TradeRows, TradeWidget, TradeWidgetSlots, useGetReceiveAmountInfo, useTradePriceImpact } from 'modules/trade'
import { BulletListItem, UnlockWidgetScreen } from 'modules/trade/pure/UnlockWidgetScreen'
import { useHandleSwap } from 'modules/tradeFlow'
import { useTradeQuote } from 'modules/tradeQuote'
import { SettingsTab, TradeRateDetails } from 'modules/tradeWidgetAddons'

import { useRateInfoParams } from 'common/hooks/useRateInfoParams'
import { useSafeMemoObject } from 'common/hooks/useSafeMemo'
import { CurrencyInfo } from 'common/pure/CurrencyInputPanel/types'

import { CoWAmmInlineBanner, SelectAPoolButton } from './elements'

import { usePoolsInfo } from '../../hooks/usePoolsInfo'
import { useVampireAttack, useVampireAttackFirstTarget } from '../../hooks/useVampireAttack'
import { useYieldDerivedState } from '../../hooks/useYieldDerivedState'
import {
  useYieldDeadlineState,
  useYieldRecipientToggleState,
  useYieldSettings,
  useYieldUnlockState,
} from '../../hooks/useYieldSettings'
import { useYieldWidgetActions } from '../../hooks/useYieldWidgetActions'
import { PoolApyPreview } from '../../pure/PoolApyPreview'
import { TargetPoolPreviewInfo } from '../../pure/TargetPoolPreviewInfo'
import { TradeButtons } from '../TradeButtons'
import { Warnings } from '../Warnings'
import { YieldConfirmModal } from '../YieldConfirmModal'

const YIELD_BULLET_LIST_CONTENT_MSG: Array<{ content: MessageDescriptor }> = [
  { content: msg`Maximize your yield on existing LP positions` },
  { content: msg`Seamlessly swap your tokens into CoW AMM pools` },
  { content: msg`Earn higher returns with reduced impermanent loss` },
  { content: msg`Leverage advanced strategies for optimal growth` },
]

const YIELD_UNLOCK_SCREEN: {
  id: string
  title: MessageDescriptor
  subtitle: MessageDescriptor
  orderType: MessageDescriptor
  buttonText: MessageDescriptor
} = {
  id: `yield-widget`,
  title: msg`Unlock Enhanced Yield Features`,
  subtitle: msg`Boooost your current LP positions with CoW AMM’s pools.`,
  orderType: msg`yield`,
  buttonText: msg`Start boooosting your yield!`,
}

// TODO: Break down this large function into smaller functions
// TODO: Add proper return type annotation
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line max-lines-per-function, @typescript-eslint/explicit-function-return-type, complexity
export function YieldWidget() {
  const { i18n } = useLingui()
  const { chainId, account } = useWalletInfo()
  const { showRecipient } = useYieldSettings()
  const deadlineState = useYieldDeadlineState()
  const recipientToggleState = useYieldRecipientToggleState()
  const [isUnlocked, setIsUnlocked] = useYieldUnlockState()
  const { isLoading: isRateLoading } = useTradeQuote()
  const priceImpact = useTradePriceImpact()
  const widgetActions = useYieldWidgetActions()
  const receiveAmountInfo = useGetReceiveAmountInfo()
  const poolsInfo = usePoolsInfo()
  const vampireAttackContext = useVampireAttack()
  const vampireAttackTarget = useVampireAttackFirstTarget()
  const { disableCustomRecipient } = useInjectedWidgetParams()

  const {
    inputCurrency,
    outputCurrency,
    inputCurrencyAmount,
    outputCurrencyAmount,
    inputCurrencyBalance,
    outputCurrencyBalance,
    inputCurrencyFiatAmount,
    outputCurrencyFiatAmount,
    recipient,
    recipientAddress,
  } = useYieldDerivedState()
  const doTrade = useHandleSwap(useSafeMemoObject({ deadline: deadlineState[0] }), widgetActions)

  const inputPoolState = useMemo(() => {
    if (!poolsInfo || !inputCurrency) return null

    return poolsInfo[getAddressKey(getCurrencyAddress(inputCurrency))]
  }, [inputCurrency, poolsInfo])

  const outputPoolState = useMemo(() => {
    if (!poolsInfo || !outputCurrency) return null

    return poolsInfo[getAddressKey(getCurrencyAddress(outputCurrency))]
  }, [outputCurrency, poolsInfo])

  const isOutputLpToken = Boolean(outputCurrency && outputCurrency instanceof LpToken)
  const inputApy = inputPoolState?.info.apy
  const outputApy = outputPoolState?.info.apy

  const isTradeContainAlternativePool =
    inputCurrency instanceof LpToken &&
    outputCurrency instanceof LpToken &&
    inputCurrency.tokens.every((token) => outputCurrency.tokens.includes(token))

  const YIELD_BULLET_LIST_CONTENT = YIELD_BULLET_LIST_CONTENT_MSG.map(
    ({ content }) => ({ content: i18n._(content) }) as BulletListItem,
  )

  const inputCurrencyInfo: CurrencyInfo = {
    field: Field.INPUT,
    currency: inputCurrency,
    amount: inputCurrencyAmount,
    isIndependent: true,
    balance: inputCurrencyBalance,
    fiatAmount: inputCurrencyFiatAmount,
    receiveAmountInfo: null,
    topContent: inputCurrency && (
      <TargetPoolPreviewInfo chainId={chainId} sellToken={inputCurrency}>
        <PoolApyPreview
          apy={inputApy}
          isSuperior={Boolean(
            isTradeContainAlternativePool &&
              inputCurrency.lpTokenProvider === LpTokenProvider.COW_AMM &&
              (inputApy && outputApy ? inputApy > outputApy : true),
          )}
        />
      </TargetPoolPreviewInfo>
    ),
  }

  const outputCurrencyInfo: CurrencyInfo = {
    field: Field.OUTPUT,
    currency: outputCurrency,
    amount: outputCurrencyAmount,
    isIndependent: false,
    balance: outputCurrencyBalance,
    fiatAmount: outputCurrencyFiatAmount,
    receiveAmountInfo,
    topContent: outputCurrency ? (
      <TargetPoolPreviewInfo chainId={chainId} sellToken={outputCurrency} oppositeToken={inputCurrency}>
        <PoolApyPreview
          apy={outputApy}
          isSuperior={Boolean(
            isTradeContainAlternativePool &&
              outputCurrency.lpTokenProvider === LpTokenProvider.COW_AMM &&
              (inputApy && outputApy ? outputApy > inputApy : true),
          )}
        />
      </TargetPoolPreviewInfo>
    ) : null,
  }
  const inputCurrencyPreviewInfo = {
    amount: inputCurrencyInfo.amount,
    fiatAmount: inputCurrencyInfo.fiatAmount,
    balance: inputCurrencyInfo.balance,
    label: t`Sell amount`,
  }

  const outputCurrencyPreviewInfo = {
    amount: outputCurrencyInfo.amount,
    fiatAmount: outputCurrencyInfo.fiatAmount,
    balance: outputCurrencyInfo.balance,
    label: t`Receive (before fees)`,
  }

  const rateInfoParams = useRateInfoParams(inputCurrencyInfo.amount, outputCurrencyInfo.amount)
  const slots: TradeWidgetSlots = {
    topContent: vampireAttackContext ? (
      <CoWAmmInlineBanner token={vampireAttackTarget?.target.token} apyDiff={vampireAttackTarget?.apyDiff} />
    ) : !account ? (
      <CoWAmmInlineBanner token={undefined} apyDiff={undefined} />
    ) : null,
    selectTokenWidget: <SelectTokenWidget displayLpTokenLists />,
    settingsWidget: (
      <SettingsTab
        recipientToggleState={recipientToggleState}
        deadlineState={deadlineState}
        isRecipientToggleHidden={disableCustomRecipient}
      />
    ),
    bottomContent: useCallback(
      (tradeWarnings: ReactNode | null, captcha: ReactNode | null) => {
        return (
          <>
            <TradeRows>
              <TradeRateDetails
                isTradePriceUpdating={isRateLoading}
                rateInfoParams={rateInfoParams}
                deadline={deadlineState[0]}
              />
            </TradeRows>
            <Warnings />
            {tradeWarnings}
            {captcha}
            <TradeButtons isOutputLpToken={isOutputLpToken} isTradeContextReady={doTrade.contextIsReady} />
          </>
        )
      },
      [doTrade.contextIsReady, isRateLoading, rateInfoParams, deadlineState, isOutputLpToken],
    ),

    lockScreen: !isUnlocked ? (
      <UnlockWidgetScreen
        id={YIELD_UNLOCK_SCREEN.id}
        items={YIELD_BULLET_LIST_CONTENT}
        handleUnlock={() => setIsUnlocked(true)}
        title={i18n._(YIELD_UNLOCK_SCREEN.title)}
        subtitle={i18n._(YIELD_UNLOCK_SCREEN.subtitle)}
        orderType={i18n._(YIELD_UNLOCK_SCREEN.orderType)}
        buttonText={i18n._(YIELD_UNLOCK_SCREEN.buttonText)}
      />
    ) : undefined,
  }

  const params = {
    compactView: true,
    enableSmartSlippage: true,
    displayTokenName: true,
    isMarketOrderWidget: true,
    recipient,
    showRecipient,
    isTradePriceUpdating: isRateLoading,
    priceImpact,
    customSelectTokenButton: SelectAPoolButton,
  }

  return (
    <TradeWidget
      disableOutput
      slots={slots}
      actions={widgetActions}
      params={params}
      inputCurrencyInfo={inputCurrencyInfo}
      outputCurrencyInfo={outputCurrencyInfo}
      confirmModal={
        <YieldConfirmModal
          doTrade={doTrade.callback}
          recipient={recipient}
          recipientAddress={recipientAddress}
          priceImpact={priceImpact}
          inputCurrencyInfo={inputCurrencyPreviewInfo}
          outputCurrencyInfo={outputCurrencyPreviewInfo}
        />
      }
    />
  )
}
